import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from './api'
import type { PortalConfig, PortalUser, ViolationType } from './types'

interface PortalContextValue {
  config: PortalConfig | null
  user: PortalUser | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  can: (permission: string) => boolean
  violationByCode: (code: string) => ViolationType | undefined
  violationLabel: (code: string) => string
}

const PortalContext = createContext<PortalContextValue | null>(null)

export function PortalProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PortalConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setError(null)
      setConfig(await api.config())
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not reach the portal server.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const value = useMemo<PortalContextValue>(() => {
    const index = new Map((config?.catalog.violations ?? []).map((type) => [type.code, type]))
    const user = config?.user ?? null
    const permissions = new Set(user?.permissions ?? [])
    return {
      config,
      user,
      loading,
      error,
      reload,
      login: async (email: string, password: string) => {
        await api.login(email, password)
        await reload()
      },
      logout: async () => {
        await api.logout()
        await reload()
      },
      can: (permission: string) => permissions.has('*') || permissions.has(permission),
      violationByCode: (code: string) => index.get(code),
      violationLabel: (code: string) => index.get(code)?.label ?? code,
    }
  }, [config, loading, error, reload])

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
}

export function usePortal() {
  const context = useContext(PortalContext)
  if (!context) throw new Error('usePortal must be used inside <PortalProvider>')
  return context
}
